import { getLocale, getTranslations } from "next-intl/server"

import { CatDisclosure } from "@/components/marketplace/layout/cat-disclosure"
import {
  APP_STORE_LINKS,
  FOOTER_SOCIAL,
  FooterNavLink,
  getFooterColumns,
  getShopCategoryLinks,
  LEGAL_DISCLOSURE,
  type FooterColumn,
} from "@/components/marketplace/layout/footer-links"
import { StoreBadges } from "@/components/marketplace/layout/store-badges"
import { Wordmark } from "@/components/marketplace/layout/wordmark"
import { HairlinePlusIcon, Row, Stack, Text } from "@/components/ui"

// The tap area spans the 30px pitch exactly (4px above and below the 22px line): hit-target's 44px
// overlay would overlap the neighbouring links.
const linkClass = "font-text text-footer-link text-fg-heading hover:text-fg-brand relative flex items-center after:absolute after:inset-x-0 after:-inset-y-1"

const ColumnLinks = ({ column, className }: { column: FooterColumn; className?: string }) => (
  <Stack as="ul" gap="200" className={className}>
    {column.links.map((link) => (
      <li key={link.label}>
        <FooterNavLink link={link} className={linkClass} />
      </li>
    ))}
  </Stack>
)

const ShopCategoryLinks = ({ heading, links }: { heading: string; links: FooterColumn["links"] }) => (
  <nav aria-label={heading}>
    <Text as="h2" family="text" tone="inherit" className="text-column-head text-fg-heading/60 leading-6.75">
      {heading}
    </Text>
    <Row as="ul" gap="200" wrap className="mt-400 gap-x-500">
      {links.map((link) => (
        <li key={link.href}>
          <FooterNavLink link={link} className={linkClass} />
        </li>
      ))}
    </Row>
  </nav>
)

const SocialRow = ({ className }: { className?: string }) => (
  <Row as="ul" gap="250" wrap className={className}>
    {FOOTER_SOCIAL.map((social) => (
      <li key={social.label}>
        <a
          href={social.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={social.label}
          className="bg-surface-social text-fg-inverted hover:bg-brand duration-quick ease-productive hit-target flex size-8 items-center justify-center rounded-full transition-colors"
        >
          {social.icon}
        </a>
      </li>
    ))}
  </Row>
)

const SocialLinks = ({ heading, align = "start", className }: { heading: string; align?: "start" | "center"; className?: string }) => (
  <Stack gap="0" align={align} className={className}>
    <Text as="h2" family="text" tone="inherit" className="sr-only">
      {heading}
    </Text>
    <SocialRow />
  </Stack>
)

const DesktopColumns = ({ columns, navigationLabel, followUsHeading }: { columns: FooterColumn[]; navigationLabel: string; followUsHeading: string }) => (
  <nav aria-label={navigationLabel} className="hidden @3xl:block">
    <div className="@5xl:grid-cols-footer grid grid-cols-2 gap-800 @5xl:gap-x-500">
      {columns.map((column, index) => (
        <div key={column.heading}>
          <Text as="h2" family="text" tone="inherit" className="text-column-head text-fg-heading/60 leading-6.75">
            {column.heading}
          </Text>
          <ColumnLinks column={column} className="mt-400" />
          {index === columns.length - 1 && <SocialLinks heading={followUsHeading} className="mt-1250" />}
        </div>
      ))}
    </div>
  </nav>
)

const MobileColumns = ({ columns, navigationLabel }: { columns: FooterColumn[]; navigationLabel: string }) => (
  <nav aria-label={navigationLabel} className="@3xl:hidden">
    <Stack gap="250">
      {columns.map((column, index) => (
        <details key={column.heading} open={index === 0} className="group disclosure ring-line rounded-8x ring-1 ring-inset">
          <summary className="focus-visible:ring-brand rounded-8x flex cursor-pointer list-none items-center gap-400 p-500 group-open:pb-100 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset">
            <Text as="h2" family="text" tone="inherit" className="text-column-head text-fg-heading/60 flex-1">
              {column.heading}
            </Text>
            <span aria-hidden className="text-fg-brand duration-quick ease-productive grid size-6 shrink-0 place-items-center transition-transform group-open:rotate-45">
              <HairlinePlusIcon size={16} />
            </span>
          </summary>
          <ColumnLinks column={column} className="pt-275 pr-500 pb-500 pl-450" />
        </details>
      ))}
    </Stack>
  </nav>
)

const FooterNavigation = ({ columns, navigationLabel, followUsHeading }: { columns: FooterColumn[]; navigationLabel: string; followUsHeading: string }) => (
  <>
    <DesktopColumns columns={columns} navigationLabel={navigationLabel} followUsHeading={followUsHeading} />
    <MobileColumns columns={columns} navigationLabel={navigationLabel} />
  </>
)

export const Footer = async () => {
  const t = await getTranslations("footer")
  const columns = getFooterColumns(t, await getLocale())

  return (
    <footer className="bg-surface text-fg py-1000 @3xl:py-1250">
      <div className="px-gut max-w-page mx-auto w-full">
        <div className="pb-600">
          <CatDisclosure />
        </div>
        <div className="pb-800">
          <ShopCategoryLinks heading={t("shopByCategoryHeading")} links={getShopCategoryLinks(t)} />
        </div>
        <FooterNavigation columns={columns} navigationLabel={t("navigationLabel")} followUsHeading={t("followUsHeading")} />

        <Stack gap="500" align="center" className="mt-1000 @3xl:mt-1250 @3xl:items-start">
          <SocialLinks heading={t("followUsHeading")} align="center" className="@3xl:hidden" />
          <StoreBadges size="lg" links={APP_STORE_LINKS} />
        </Stack>

        <Stack gap="200" className="mt-1000 @3xl:mt-1250">
          {LEGAL_DISCLOSURE.map((paragraph) => (
            <Text key={paragraph} as="p" family="text" tone="inherit" className="text-little-lg text-fg-legal text-center font-normal @3xl:text-left">
              {paragraph}
            </Text>
          ))}
        </Stack>

        <Stack gap="250" align="center" className="mt-1000 @3xl:mt-1250">
          <Wordmark tone="blue" height={40} />
          <Text as="span" family="text" tone="inherit" className="text-little-lg text-fg-legal font-normal">
            {t("copyright")}
          </Text>
        </Stack>
      </div>
    </footer>
  )
}
